import {
  PC_13663,
  PRICING,
  SDPD,
  SDPD_AXON,
  SDPD_LICENSE_AT_FULL_SWORN,
} from "./data";
import { C, fmtInt, fmtMoney } from "./theme";
import { Callout, Card, Eyebrow, Section, Source, Step, SubSection, Table, Td } from "./ui";

// ===================================================================
// 09. Security and compliance
// ===================================================================

const CJIS_CONTROLS = [
  {
    cite: "§ 5.4.1.1.1",
    area: "Auditable events and retention",
    control:
      "Sixteen security event types are logged (authentication, MFA challenge, access denial, session expiry, privilege change, account lifecycle, and concurrent-session detection), each with actor, agency, session, source address, and user agent. Infrastructure audit logs are retained well beyond the one-year floor.",
  },
  {
    cite: "§ 5.5.2 / § 5.6",
    area: "Access control and identification",
    control:
      "Multi-factor authentication with agency-level enforcement: authenticator app, platform biometrics and security keys, or email one-time code, with backup codes and revocable trusted devices. Single sign-on via SAML 2.0 or OIDC. Role-based access separates officers, who see their own cases, from records personnel, who see the agency's.",
  },
  {
    cite: "§ 5.5.5 / § 5.6",
    area: "Session management",
    control:
      "Twelve-hour maximum session duration and thirty-minute inactivity timeout, enforced server-side at three independent layers. Password policy requires twelve characters with complexity and screens against known-breached credentials. Account lockout after repeated failures.",
  },
  {
    cite: "§ 5.10.1",
    area: "Boundary protection",
    control:
      "Deployed in AWS GovCloud (US-West). HTTPS-only with a one-year strict transport policy. Database connections use TLS with certificate verification enforced. Firewall rulesets are reviewed annually, rule by rule, against documented justification.",
  },
  {
    cite: "§ 5.10.4.1",
    area: "Media sanitization",
    control:
      "Agency-configurable retention. Deletion removes the database record and every associated object in storage (source audio, extracted audio, transcripts, and attachments), not merely the pointer to them.",
  },
];

// ===================================================================
// § 13663 artifacts
//
// These render the real thing rather than describing it: the exact
// disclosure text the statute requires, the attestation gate an officer
// actually passes through, and the shape of the rows we write. A procurement
// reader can compare them against the statute line by line.
// ===================================================================

/** The exact language required by § 13663(a), verbatim. */
const STATUTORY_DISCLOSURE =
  "This report was written either fully or in part using artificial intelligence.";

/** The affirmation an officer accepts before any export completes. */
const OFFICER_AFFIRMATION =
  "I affirm that this report, generated with the assistance of Police Narratives AI, " +
  "has been thoroughly reviewed and edited by me to ensure its accuracy in representing " +
  "my recollection of the reported events. I acknowledge the potential legal implications " +
  "of this statement and, if required, am prepared to testify to its accuracy.";

function ComplianceItem({
  cite,
  requirement,
  how,
  artifactLabel,
  children,
}: {
  cite: string;
  requirement: string;
  how: string;
  artifactLabel?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden break-inside-avoid">
      <div className="px-5 py-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1.5">
          <span className="font-mono text-xs font-bold text-slate-900 whitespace-nowrap">
            {cite}
          </span>
          <span className="text-sm font-semibold text-slate-800">{requirement}</span>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{how}</p>
      </div>
      {children && (
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-4">
          {artifactLabel && (
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2.5">
              {artifactLabel}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

/** Mock of the disclosure as it lands on an exported report. */
function DocumentSnippet() {
  return (
    <div className="bg-white border border-slate-300 rounded shadow-sm p-4 max-w-2xl">
      <div className="text-[11px] text-slate-400 leading-relaxed">
        …the suspect was transported to County Jail without further incident. Nothing
        further to report.
      </div>
      <div className="my-3 border-t border-dashed border-slate-300" />
      <p className="text-[12px] text-slate-900 leading-relaxed">
        <strong>{STATUTORY_DISCLOSURE}</strong> {OFFICER_AFFIRMATION}
      </p>
      <div className="mt-3 pt-2 border-t border-slate-200 flex flex-wrap gap-x-6 gap-y-1 text-[10px] text-slate-500">
        <span>
          AI program: <span className="font-semibold text-slate-700">Police Narratives AI</span>
        </span>
        <span>
          Reviewed and signed:{" "}
          <span className="font-semibold text-slate-700">Officer J. Rivera, #4471</span>
        </span>
      </div>
    </div>
  );
}

/** Mock of the review-and-acknowledge gate that precedes every export. */
function AttestationDialog() {
  return (
    <div className="bg-white border border-slate-300 rounded-lg shadow-md max-w-lg overflow-hidden">
      <div className="px-5 pt-4 pb-3">
        <div className="text-sm font-bold text-slate-900 mb-2">Review and acknowledge</div>
        <p className="text-[12px] text-slate-600 leading-relaxed">{OFFICER_AFFIRMATION}</p>
      </div>
      <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
        <span className="px-3 py-1.5 rounded border border-slate-300 bg-white text-xs font-semibold text-slate-600">
          Cancel
        </span>
        <span
          className="px-3 py-1.5 rounded text-xs font-semibold text-white"
          style={{ backgroundColor: C.primary }}
        >
          Agree and download PDF
        </span>
      </div>
    </div>
  );
}

/** Renders a single database row as key/value pairs. */
function DbRow({
  table,
  rows,
  note,
}: {
  table: string;
  rows: [string, string, boolean?][];
  note?: string;
}) {
  return (
    <div>
      <div className="bg-slate-900 rounded-md overflow-hidden max-w-2xl">
        <div className="px-3 py-1.5 border-b border-slate-700 font-mono text-[10px] text-slate-400">
          {table}
        </div>
        <div className="p-3 font-mono text-[11px] leading-relaxed overflow-x-auto">
          {rows.map(([k, v, hi]) => (
            <div key={k} className="flex gap-3">
              <span className="text-slate-500 shrink-0 w-28">{k}</span>
              <span className={hi ? "text-emerald-300 font-bold" : "text-slate-200"}>
                {v}
              </span>
            </div>
          ))}
        </div>
      </div>
      {note && <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">{note}</p>}
    </div>
  );
}

/** Reconstructed audit trail for one case. */
function AuditTrail() {
  const events = [
    ["2026-03-14 21:31:52", "case_files", "bodycam X2-4471-0314.mp4 linked to case"],
    ["2026-03-14 21:44:10", "case_files", "narrative dictation rivera-0314.m4a linked to case"],
    ["2026-03-14 21:47:03", "first_draft", "AI draft generated · officer 6b2e91f4 · score 78"],
    ["2026-03-14 22:02:19", "suggestions_review", "3 proposed changes, 2 accepted · officer 6b2e91f4"],
    ["2026-03-14 22:09:47", "exported", "PDF export, attestation accepted · officer 6b2e91f4"],
  ];
  return (
    <div>
      <div className="bg-slate-900 rounded-md overflow-hidden">
        <div className="p-3 font-mono text-[11px] leading-relaxed overflow-x-auto">
          {events.map(([ts, type, detail]) => (
            <div key={ts} className="flex gap-3 whitespace-nowrap">
              <span className="text-slate-500 shrink-0">{ts}</span>
              <span className="text-amber-300 shrink-0 w-40">{type}</span>
              <span className="text-slate-200">{detail}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
        Who used AI, when, on which report, against which recordings, and what they did
        with the result. Produced on request for discovery, internal affairs, or a records
        audit.
      </p>
    </div>
  );
}

export function SecuritySection() {
  return (
    <Section
      id="security"
      number="09"
      title="Security, privacy, and California law"
      subtitle={
        <>
          Criminal justice information does not leave a controlled environment, agency
          data is never used to train a model, and the record required to defend an
          AI-assisted report exists by default rather than by configuration.
        </>
      }
    >
      <SubSection
        title="Where the system runs"
        subtitle="AWS GovCloud (US-West), end to end: storage, database, application, and AI processing."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card title="AWS GovCloud">
            All processing, including AI inference on Amazon Bedrock, runs inside the
            GovCloud (US-West) region.
          </Card>
          <Card title="Encrypted at rest">
            Customer-managed encryption keys, distinct per storage bucket, with versioning
            and public access blocked entirely.
          </Card>
          <Card title="Encrypted in transit">
            HTTPS-only with a one-year strict transport policy; database connections
            verify certificates.
          </Card>
          <Card title="Independently tested">
            Penetration testing completed and passed. The report is available to the City
            under a non-disclosure agreement.
          </Card>
        </div>
      </SubSection>

      <SubSection
        title="CJIS-aligned controls"
        subtitle="Mapped to the specific sections a CJIS audit will ask about."
      >
        <Table minWidth={700} head={["CJIS §", "Control area", "How it is implemented"]}>
          {CJIS_CONTROLS.map((c) => (
            <tr key={c.cite}>
              <Td strong className="whitespace-nowrap font-mono text-xs">
                {c.cite}
              </Td>
              <Td strong className="whitespace-nowrap">
                {c.area}
              </Td>
              <Td>{c.control}</Td>
            </tr>
          ))}
        </Table>
        <Source>
          Full architecture documentation, network topology, vulnerability management
          procedure with remediation SLAs, access-grant policy, data retention policy, and
          the penetration test report are available to the City under NDA. Detailed
          infrastructure specifics are withheld from this public document under Government
          Code § 7929.210.
        </Source>
      </SubSection>

      <SubSection
        title={`${PC_13663.codeSection} compliance`}
        subtitle={
          <>
            Element by element. We advised Senator Arreguín's office during the drafting
            of {PC_13663.billName} and built to these requirements before they took effect.
          </>
        }
      >
        <div className="space-y-4">
          <ComplianceItem
            cite="§ 13663(a)"
            requirement="AI-use disclosure naming every AI program used"
            how="Exports from California agencies automatically carry the statutory disclosure. It is applied by the platform on the way out, so an officer cannot omit it by exporting a different way."
            artifactLabel="What appears on the report"
          >
            <DocumentSnippet />
          </ComplianceItem>

          <ComplianceItem
            cite="§ 13663(a)(2)"
            requirement="Officer signs, verifying review and that the facts are true and correct"
            how="Every export path is gated behind a review-and-acknowledge step. The officer cannot reach a PDF, a Word file, the clipboard, an email, or a mobile share without passing through it. The acknowledgment, with officer identity and timestamp, is what is recorded as the signature."
            artifactLabel="What the officer sees before any export"
          >
            <AttestationDialog />
          </ComplianceItem>

          <ComplianceItem
            cite="§ 13663(b)(1)"
            requirement="Agency retains the first draft for as long as the report is maintained"
            how="The first AI-generated draft is written to an append-only audit record at the moment it is produced, with the full text preserved. Officer edits write to the case record and never touch it."
            artifactLabel="The row written to the audits table"
          >
            <DbRow
              table="audits"
              rows={[
                ["id", "a7f3c2e1-4b8d-4f2a-9c31-0e5d7a8b9c04"],
                ["case_id", "3d91b6c8-2a47-4e15-b8f9-71c0d4e2a635"],
                ["user_id", "6b2e91f4-8c05-4a73-9d16-2f8a0b3c5d97"],
                ["audit_type", "first_draft", true],
                ["created_at", "2026-03-14 21:47:03.118+00"],
                ["score", "78"],
                [
                  "content",
                  "On 03/14/2026 at approximately 2118 hours, I was dispatched to the 4200 block of El Cajon Boulevard regarding a report of a disturbance. Upon arrival I contacted the reporting party, who stated…",
                ],
              ]}
              note="content holds the complete first draft as generated, not a summary or a diff."
            />
          </ComplianceItem>

          <ComplianceItem
            cite="§ 13663(c)"
            requirement="Audit trail identifying who used AI and the footage relied on"
            how="Every AI event on a case is written with the officer who initiated it and a timestamp, and the source recordings stay linked to the case for as long as the report is retained. The trail below reconstructs one report end to end."
            artifactLabel="The trail for a single case"
          >
            <AuditTrail />
          </ComplianceItem>

          <ComplianceItem
            cite="§ 13663(d)(1)"
            requirement="Vendor may not share, sell, or otherwise use agency information"
            how="We do not sell, share, or otherwise use SDPD data except for SDPD's own purposes or pursuant to a court order. No agency data is used to train any model. These restrictions flow down contractually to every subprocessor, and we will accept them as an express warranty with a remedy rather than as a policy statement."
          />
        </div>

        <div className="mt-6">
          <Callout tone="good" title="Retention as a managed service">
            <p>
              SDPD does not have to build a process to hold AI drafts. We retain the first
              draft and the final attested version on the department's behalf for the life
              of the report, as part of the service.
            </p>
            <p>
              When a draft is needed, whether for a defense request, an internal affairs
              review, or a records audit, the department contacts{" "}
              <span className="font-semibold text-slate-900">audits@policenarratives.ai</span>{" "}
              and we produce it. That is a support obligation we take on, not a workflow
              the department has to operate.
            </p>
          </Callout>
        </div>
      </SubSection>

      <SubSection
        title="A recommended configuration note"
        subtitle="One setting has statutory consequences for a California agency."
      >
        <Callout tone="warning">
          <p>
            The platform supports an optional delete-on-export mode that permanently
            removes a case and its source audio once the report is exported. Some agencies
            want it; for a California agency subject to {PC_13663.codeSection} we recommend
            leaving it <strong>off</strong>, so that source recordings remain available for
            as long as the report is retained.
          </p>
          <p>
            We would rather raise this during procurement than have it discovered during
            discovery.
          </p>
        </Callout>
      </SubSection>
    </Section>
  );
}

// ===================================================================
// 10. Integration
// ===================================================================

export function IntegrationSection() {
  return (
    <Section
      id="integration"
      number="10"
      title="RMS-agnostic by design"
      subtitle={
        <>
          SDPD asked about integration with report submission and retention databases. Our
          answer is deliberate: the platform is not coupled to any records management
          system, and we think that is the right architecture for a department of this
          size.
        </>
      }
    >
      <div className="grid md:grid-cols-2 gap-6 items-start">
        <div>
          <Eyebrow>Why we built it this way</Eyebrow>
          <p className="text-sm text-slate-700 leading-relaxed">
            A report-writing platform welded to one RMS becomes a reason not to change the
            RMS. Departments end up making a records-system decision to protect a
            report-writing investment, or a report-writing decision to protect a records
            contract. Neither is a good position.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed mt-3">
            Keeping the two independent means SDPD can adopt, replace, or consolidate
            records systems without touching how officers write reports, and can change
            report-writing platforms without an RMS migration. The department keeps both
            decisions.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 p-5">
          <Eyebrow>How reports leave the platform</Eyebrow>
          <ul className="space-y-2.5">
            {[
              ["PDF", "Formatted to the agency's report layout, with redactions applied if selected."],
              ["Microsoft Word", "Editable document for workflows that require one."],
              ["Formatted clipboard", "Paste directly into an RMS narrative field with formatting intact."],
              ["Email", "Delivered to the officer or to a designated address."],
            ].map(([k, v]) => (
              <li key={k} className="flex gap-3">
                <span className="shrink-0 w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: C.primary }} />
                <span className="text-sm">
                  <span className="font-semibold text-slate-900">{k}</span>
                  <span className="text-slate-600">: {v}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">
            Every export is written to the audit trail with the officer, the timestamp, and
            the exported content.
          </p>
        </div>
      </div>

      <SubSection
        title="Where we already connect"
        subtitle="On the evidence side, integration is live."
      >
        <Callout tone="good">
          <p>
            SDPD operates more than {SDPD_AXON.bodyCamerasInService.toLocaleString()} Axon
            body-worn cameras with evidence on {SDPD_AXON.evidencePlatform}. Our Axon
            Evidence integration is in production with other agencies today. The department
            creates an API key once, we test the connection before enabling it, and from
            then on officers browse their own evidence locker from inside the platform and
            select the recordings for a case.
          </p>
          <p>
            Nothing is downloaded and nothing is re-uploaded. The transfer runs server-side
            between Axon and us, which means no officer waits on a multi-gigabyte export, no
            evidence file lands on a laptop, and no second copy of evidence is created
            outside the evidence management system.
          </p>
        </Callout>
      </SubSection>

      <SubSection
        title="What we would build for SDPD, and what we need to know"
        subtitle="If the City wants tighter coupling than export, these are the options in the order we would sequence them."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
          <Step n={1} title="Structured field export">
            Report content emitted as structured data mapped to SDPD's report formats,
            including ARJIS-9, rather than as a document.
          </Step>
          <Step n={2} title="Authenticated push API">
            A department-credentialed interface that submits completed reports directly.
          </Step>
          <Step n={3} title="Direct RMS connector" color={C.accent}>
            Built against the department's specific records system, subject to what that
            vendor exposes.
          </Step>
          <Step n={4} title="Additional evidence vendors" color={C.accent}>
            The same pattern used for Axon, extended to other in-car and body-worn
            platforms.
          </Step>
        </div>

        <div className="mt-6">
          <Callout tone="good" title="We will build what the department needs">
            <p>
              RMS-agnostic is our default posture, not a limit on what we will do. We keep
              an integrations team available for exactly this work, and we treat connector
              development as part of the engagement rather than as a change order.
            </p>
            <p>
              If SDPD's records system, ARJIS, or any other departmental platform exposes
              API endpoints, tell us which ones and we will build against them. If the
              department needs an endpoint on our side, we will build that too. We have no
              interest in making the department's data harder to move.
            </p>
          </Callout>
        </div>

        <div className="mt-6">
          <Callout tone="legal" title="Three questions the City should answer in the solicitation">
            <p>
              These determine what any respondent can actually commit to, and we would
              rather the City ask them of everyone than have each vendor guess:
            </p>
            <ol className="list-decimal ml-5 space-y-1.5 text-sm">
              <li>
                Which records management system will SDPD be operating during the contract
                term, and does it expose a documented write interface?
              </li>
              <li>
                Is submission into ARJIS, including the ARJIS-9 officer-only report, in
                scope, and who owns the field mapping?
              </li>
              <li>
                Who is responsible for integration work when the RMS vendor's cooperation
                is required: the report-writing vendor, the RMS vendor, or the City?
              </li>
            </ol>
            <p className="text-xs text-slate-600">
              SDPD's FY2026 budget carries roughly $2.0 million for ARJIS and eSUN network
              costs, so the regional integration question is a live one rather than
              hypothetical.
            </p>
          </Callout>
        </div>
      </SubSection>
    </Section>
  );
}

// ===================================================================
// 13. Implementation
// ===================================================================

const PHASES = [
  {
    phase: "Phase 1",
    title: "Configuration",
    weeks: "Weeks 1–3",
    items: [
      "Report templates built to SDPD formats, including ARJIS-9",
      "Agency preferences, checklists, and snippets configured",
      "Single sign-on and multi-factor policy set to department standard",
      "Axon Evidence connection configured and tested",
      "Department policies and statute libraries uploaded",
    ],
  },
  {
    phase: "Phase 2",
    title: "Measured pilot",
    weeks: "Weeks 4–12",
    items: [
      "One division, with a defined baseline measured before go-live",
      "Live onboarding and roll-call-length training sessions",
      "Field training officers trained as internal trainers",
      "Weekly adoption and quality reporting to command staff",
      "Configuration tuned against what officers actually produce",
    ],
  },
  {
    phase: "Phase 3",
    title: "Expansion",
    weeks: "Months 4–8",
    items: [
      "Division-by-division rollout on the department's schedule",
      "Internal trainers lead onboarding, with us supporting",
      "Investigative units brought on with their own templates",
      "Forms and redaction workflows configured for records personnel",
    ],
  },
  {
    phase: "Phase 4",
    title: "Steady state",
    weeks: "Ongoing",
    items: [
      "Department-wide availability",
      "Quarterly reviews of adoption, report quality, and time recovered",
      "New capabilities delivered continuously at no additional cost",
      "Annual compliance evidence package for CJIS and records audits",
    ],
  },
];

export function RolloutSection() {
  return (
    <Section
      id="rollout"
      number="13"
      title="Implementation, training, and support"
      subtitle="A department of this size does not turn something like this on all at once, and should not be asked to."
    >
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {PHASES.map((p, i) => (
          <div
            key={p.phase}
            className="rounded-lg border border-slate-200 p-4 border-t-4"
            style={{ borderTopColor: i === 1 ? C.accent : C.primary }}
          >
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <span
                className="text-[10px] uppercase tracking-wider font-bold"
                style={{ color: i === 1 ? C.accent : C.primary }}
              >
                {p.phase}
              </span>
              <span className="text-[10px] text-slate-400 font-mono">{p.weeks}</span>
            </div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2.5">{p.title}</h4>
            <ul className="space-y-1.5">
              {p.items.map((it) => (
                <li key={it} className="flex gap-2 text-xs text-slate-600 leading-relaxed">
                  <span className="text-slate-300 shrink-0">·</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <SubSection title="Training" subtitle="Officers do not attend a course to learn to talk.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card title="Roll-call sessions">
            Short, in-person, at shift change. The core workflow takes minutes to teach
            because it is dictation.
          </Card>
          <Card title="Train the trainer">
            Field training officers are certified as internal trainers so the department
            owns its own onboarding.
          </Card>
          <Card title="In-app guidance">
            Guided tours introduce features in context, when an officer first encounters
            them.
          </Card>
          <Card title="Reference by report type">
            Quick-reference material for each of the report types SDPD named.
          </Card>
        </div>
      </SubSection>

      <SubSection
        title="Support, maintenance, and warranty"
        subtitle="What the City should expect to see committed in a contract."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Support">
            Named support contact, defined response targets by severity, and direct access
            to engineering for a department of this size, not a ticket queue.
          </Card>
          <Card title="Maintenance and updates">
            Continuous delivery of new capability at no additional cost. No version
            upgrades to purchase, no maintenance releases to schedule.
          </Card>
          <Card title="Regulatory services">
            SB 524 implementation support and draft production, Public Records Act
            redaction support, CJIS audit evidence packages, assistance drafting the
            department's AI-use policy, and declaration or testimony support on how a
            specific report was produced.
          </Card>
        </div>
      </SubSection>
    </Section>
  );
}

// ===================================================================
// 14. Pricing structure
// ===================================================================

function PriceTable() {
  return (
    <div>
      <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/50 p-6">
        <Eyebrow>The whole price</Eyebrow>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-2xl md:text-3xl font-semibold text-slate-700">
            Sworn officers
          </span>
          <span className="text-2xl md:text-3xl font-semibold text-slate-400">×</span>
          <span className="text-4xl md:text-5xl font-bold tabular-nums text-slate-900">
            ${PRICING.perOfficerPerYear}
          </span>
          <span className="text-sm text-slate-600">per year</span>
        </div>
        <p className="text-sm text-slate-700 leading-relaxed mt-3 max-w-2xl">
          That is the entire pricing model. One annual department license, sized by sworn
          headcount. No modules, no tiers, no per-minute or per-report charges, and nothing
          that changes when officers use the platform more.
        </p>
        <p className="text-xs text-slate-500 mt-3">
          Billed annually. Monthly billing is available at $
          {PRICING.monthlyPerOfficerPerMonth} per officer per month where a procurement
          cycle requires it.
        </p>
      </div>

      <div className="mt-4">
        <Callout title="It is a department license, not a seat count">
          <p>
            The unit we sell is the department. The rate above is how the license is sized,
            not a menu you select from, and it covers every sworn officer below command
            staff. Command staff are optional and the department decides whether to
            include them.
          </p>
          <p>
            This is a deliberate condition rather than a sales tactic. Partial deployments
            are the single most reliable way to make a platform like this fail: officers on
            a shared shift work to different standards, supervisors review reports produced
            two different ways, and the department never gets a clean baseline to evaluate
            against.
          </p>
          <p>
            There is a practical reason as well. In prior partial rollouts, licensed
            officers told unlicensed colleagues about the platform and began sharing
            accounts, notwithstanding that the contract forbids it. That is a problem for a
            department: reports get written under another officer's
            credentials, which corrupts the audit trail that{" "}
            {PC_13663.codeSection}(c) requires and undermines the attestation attached to
            every export. Licensing the full sworn complement removes the incentive
            entirely.
          </p>
        </Callout>
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
            What that works out to for San Diego, estimated
          </div>
        </div>
        <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
          <div className="p-5">
            <div className="text-2xl font-bold tabular-nums text-slate-900">
              ~{fmtMoney(SDPD_LICENSE_AT_FULL_SWORN)}
            </div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mt-1">
              Annual license
            </div>
            <p className="text-xs text-slate-600 leading-relaxed mt-2">
              {fmtInt(SDPD.swornActual)} sworn personnel as of February 2026 ×{" "}
              ${PRICING.perOfficerPerYear}. Command staff are included here and are
              optional, so electing to leave them out brings it down. SDPD's{" "}
              {fmtInt(SDPD.swornPositions)} <em>budgeted</em> positions are a larger number,
              but a funded vacancy is not an officer and is not licensed.
            </p>
          </div>
          <div className="p-5 bg-emerald-50/40">
            <div className="text-2xl font-bold tabular-nums text-emerald-700">~$9.2M</div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mt-1">
              Modeled labor recovered
            </div>
            <p className="text-xs text-slate-600 leading-relaxed mt-2">
              38.6 full-time-equivalent officers returned to patrol, on the default
              assumptions in{" "}
              <a
                href="#calculator"
                className="text-blue-700 hover:text-blue-900 underline underline-offset-2"
              >
                Section 07
              </a>
              . Change the assumptions and this number changes with them.
            </p>
          </div>
        </div>
        <div className="px-5 py-2.5 border-t border-slate-200 text-[11px] text-slate-500 leading-relaxed">
          Both figures are estimates. The license figure moves with SDPD's actual enrolled
          headcount at contract signature and with whether command staff are included; the
          right-hand figure is modeled rather than measured and represents labor recovered
          rather than cash returned to the General Fund. We put the two side by side because
          a budget analyst will do this arithmetic anyway, and we would rather they do it
          with our assumptions visible.
        </div>
      </div>
    </div>
  );
}

export function PricingSection() {
  return (
    <Section
      id="pricing"
      number="14"
      title="How we price, and how the City should require it to be priced"
      subtitle={
        <>
          One annual license for the entire department, sized by sworn headcount. The
          pricing model the City specifies will determine whether officers are encouraged
          to use the tool or discouraged from it without anyone saying so, so we have kept ours as simple as
          we can make it.
        </>
      }
    >
      <PriceTable />

      <div className="grid md:grid-cols-2 gap-6 items-start mt-6">
        <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-5">
          <Eyebrow>What the price includes</Eyebrow>
          <p className="text-base font-semibold text-slate-900 leading-snug">
            Everything in report writing. No tiers, no metering, nothing to unlock.
          </p>
          <ul className="mt-3 space-y-1.5">
            {[
              "Unlimited reports",
              "Transcription, with no per-minute charge",
              "Speaker separation on every recording",
              "Report scoring and suggestions",
              "Forms and redaction",
              "Axon Evidence integration",
              "Web and native mobile",
              "Single sign-on and multi-factor authentication",
              "All updates and new capability",
              "Support, training, and onboarding",
            ].map((x) => (
              <li key={x} className="flex gap-2 text-sm text-slate-700">
                <span className="shrink-0 font-bold" style={{ color: C.good }}>
                  ✓
                </span>
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">
            The one capability licensed separately is jail call intelligence for
            investigations (Section 11). Like report writing, it is sized per seat
            and sold department-wide: a purchase covers all of the department's
            investigators, and only investigators are counted.
          </p>
        </div>

        <div>
          <Eyebrow>What we do not meter</Eyebrow>
          <p className="text-sm text-slate-700 leading-relaxed">
            Nothing about how much an officer uses the platform changes what the
            department pays. Not audio minutes, not reports written, not transcription
            hours, not storage, not the number of times a report is re-scored.
          </p>

          <div className="mt-5">
            <Callout tone="warning" title="A caution about per-minute and per-report pricing">
              <p>
                Metered pricing puts a price on exactly the behavior the department wants:
                capturing the full interview, pulling in the body-cam footage, running the
                critique again after fixing something. Officers learn quickly that thorough
                documentation costs the department money, and they adjust.
              </p>
              <p>
                It is also unpredictable, and it can run expensive. A single body-worn
                recording can be hours long, a critical incident generates many of them,
                and the months in which the department most needs complete documentation
                are precisely the months the invoice climbs. A department cannot budget
                against that, and cannot tell in advance whether a metered bid is cheaper
                or several times more expensive than a flat one.
              </p>
              <p>
                It also makes bids incomparable. A per-minute bid cannot be evaluated
                against a per-officer bid without knowing a usage volume nobody has yet.
              </p>
              <p className="font-medium text-slate-900">
                We recommend the City require flat per-officer pricing across all
                respondents so that the bids can actually be compared.
              </p>
            </Callout>
          </div>
        </div>
      </div>

      <SubSection title="What drives our cost, disclosed" subtitle="So the City can model it honestly.">
        <div className="grid sm:grid-cols-3 gap-3">
          <Card title="Audio volume">
            The largest variable cost. It is absorbed rather than passed through, because
            metering it would distort behavior.
          </Card>
          <Card title="Retention period">
            Longer retention of source recordings increases storage cost. Configurable per
            the department's records schedule.
          </Card>
          <Card title="Support tier">
            Named contact, response targets, and on-site presence during rollout scale
            with the size of the deployment.
          </Card>
        </div>
      </SubSection>
    </Section>
  );
}
